import { apiClient } from '../services/api';
import { EmailLog, Schedule, ScheduleForm } from '../types/schedule';
import raw from './schedules.json';

/**
 * 수집 기준. **2026-09-27 부터 실제 DB 를 본다** — `USE_MOCK = false`.
 *
 * 그날 `consult_bid_schedules` 에 `exclude_keywords`·`biz_div`·
 * `classification`·`min_price`·`max_price`·`license` 를 붙이고 크론이
 * DB 를 보게 바꿨다. 그 전에는 이 여섯이 화면에만 있고 저장되지 않았다.
 *
 * 목 경로는 아직 남겨 둔다 — 개발 DB 가 없는 환경에서 화면을 보는 데 쓴다.
 * 목으로 돌리려면 USE_MOCK 을 true 로.
 */
export const USE_MOCK = false;

type MockSchedule = Schedule & { logs: EmailLog[] };

// 새로고침하면 처음으로 돌아간다 — 저장할 곳이 없으니 일부러 메모리에만 둔다
let store: MockSchedule[] = JSON.parse(JSON.stringify(raw.schedules));
let nextId = Math.max(0, ...store.map((s) => s.id)) + 1;

const delay = <T,>(value: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), 150));

const strip = ({ logs, ...rest }: MockSchedule): Schedule => rest;

export const scheduleApi = {
  list(): Promise<Schedule[]> {
    if (!USE_MOCK) return apiClient.get('/bidding/schedules');
    return delay(store.map(strip));
  },

  logs(id: number): Promise<{ items: EmailLog[] }> {
    if (!USE_MOCK) return apiClient.get(`/bidding/schedules/${id}/logs?pageSize=50`);
    return delay({ items: store.find((s) => s.id === id)?.logs ?? [] });
  },

  create(form: ScheduleForm): Promise<Schedule> {
    if (!USE_MOCK) return apiClient.post('/bidding/schedules', form);
    const created: MockSchedule = {
      ...form,
      id: nextId++,
      keywords: form.keywords || null,
      institution: form.institution || null,
      excludeKeywords: form.excludeKeywords || null,
      classification: form.classification || null,
      isActive: true,
      lastRunAt: null,
      createdAt: new Date().toISOString(),
      logs: [],
    };
    store = [...store, created];
    return delay(strip(created));
  },

  update(id: number, patch: Partial<ScheduleForm & { isActive: boolean }>): Promise<Schedule> {
    if (!USE_MOCK) return apiClient.put(`/bidding/schedules/${id}`, patch);
    store = store.map((s) => (s.id === id ? { ...s, ...patch } : s));
    return delay(strip(store.find((s) => s.id === id)!));
  },

  remove(id: number): Promise<void> {
    if (!USE_MOCK) return apiClient.delete(`/bidding/schedules/${id}`);
    store = store.filter((s) => s.id !== id);
    return delay(undefined);
  },

  /** 지금 실행 — 목에서는 0~9건을 무작위로 만들어 이력에 얹는다 */
  run(id: number): Promise<void> {
    if (!USE_MOCK) return apiClient.post(`/bidding/schedules/${id}/run`);
    const now = new Date().toISOString();
    store = store.map((s) =>
      s.id === id
        ? {
            ...s,
            lastRunAt: now,
            logs: [
              {
                id: Date.now(),
                bidCount: Math.floor(Math.random() * 10),
                emailTo: s.emailTo,
                status: 'success',
                errorMessage: null,
                sentAt: now,
              },
              ...s.logs,
            ],
          }
        : s,
    );
    return delay(undefined);
  },
};
